import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useLiveConnected, useLiveEvent } from "@/lib/live";
import type { AgentMessage } from "@/lib/types";
import { familyOf, qk, type QueryFamily } from "./keys";
import { invalidates, invalidationFor, mergeInvalidations, type Invalidation } from "./invalidation";
import { TRANSCRIPT_PAGE, upsertTranscript, type TranscriptData } from "./transcript";

const POLL_MS = 2500;
/** With the websocket up, every commit is pushed; the poll is only the safety net. */
const POLL_MS_LIVE = 15_000;
/**
 * How long invalidations are gathered before they are applied. An action of our
 * own arrives twice — its response, then the `revision` event its commit
 * published a few milliseconds later — and one refetch should answer both.
 */
const BATCH_MS = 150;
/** A working agent emits several events a second; the list is re-read at most this often. */
const AGENTS_DEBOUNCE_MS = 400;

interface LiveSync {
  /**
   * A change this client just made: the families it touched are refetched,
   * batched with the revision event the same commit publishes, so the action
   * costs one refetch per view rather than two.
   */
  localChange: (families: readonly QueryFamily[] | "all") => void;
  /** When a change someone else made last arrived — what the "live" dot shows. */
  remoteChangeAt: number | null;
  /** Moves on every applied batch; for a component still keyed on "something changed". */
  tick: number;
}

const LiveSyncContext = createContext<LiveSync>({ localChange: () => undefined, remoteChangeAt: null, tick: 0 });

export const useLiveSync = (): LiveSync => useContext(LiveSyncContext);

/** Re-reads the newest page of every transcript on screen and merges it in; drops the rest. */
function refreshTranscripts(client: QueryClient) {
  for (const query of client.getQueryCache().findAll({ queryKey: ["agent-messages"] })) {
    const agentId = query.queryKey[1];
    if (typeof agentId !== "string") continue;
    if (query.getObserversCount() === 0) {
      // Nobody is reading it; the next open fetches it whole.
      client.removeQueries({ queryKey: query.queryKey, exact: true });
      continue;
    }
    void api
      .agentMessages(agentId, { limit: TRANSCRIPT_PAGE })
      .then(({ messages }) =>
        client.setQueryData<TranscriptData>(qk.agentMessages(agentId), (data) => upsertTranscript(data, messages)),
      )
      .catch(() => undefined);
  }
}

/**
 * The change feed, applied to the cache. The MCP server and the worker write
 * straight to Postgres, so the API never sees those writes as traffic; what this
 * client hears instead is a `revision` event per commit over the websocket (naming
 * the tables it wrote), and — as the safety net — a poll of the revision counter.
 * Either one invalidates only the query families those tables feed
 * (`invalidation.ts`), and of those only what is on screen refetches; the rest is
 * marked stale for its next mount.
 *
 * Transcripts are the exception: an `agent.message` is written straight into the
 * cached pages, never refetched, and a reconnect re-reads the newest page of each.
 */
export function LiveSyncProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const connected = useLiveConnected();
  const [remoteChangeAt, setRemoteChangeAt] = useState<number | null>(null);
  const [tick, setTick] = useState(0);
  const state = useRef({
    /** The highest revision this client knows its views reflect (or are about to). */
    known: null as number | null,
    batch: null as Invalidation | null,
    /** The batch holds a change of our own, so it is not news to announce. */
    local: false,
    /** Some remote change is in the batch. */
    remote: false,
    /** An event in the batch said which revision it was, so the counter need not be read. */
    sawRevision: false,
    /** Revision events arrive at all — Redis is up and relaying. */
    eventsFlowing: false,
    timer: undefined as ReturnType<typeof setTimeout> | undefined,
  });

  const flush = useCallback(() => {
    const s = state.current;
    clearTimeout(s.timer);
    s.timer = undefined;
    if (!s.batch) return;
    // A mutation in flight will settle with its own invalidation; wait for it, so
    // the event its commit published and its settle are one refetch, not two.
    if (client.isMutating() > 0) return;
    const { batch, local, remote, sawRevision } = s;
    s.batch = null;
    s.local = false;
    s.remote = false;
    s.sawRevision = false;
    if (local && !sawRevision && !s.eventsFlowing) {
      // No push is coming to say which revision our write made; read it, or the
      // poll would see the counter move and refetch everything for our own change.
      void api
        .revision()
        .then(({ revision }) => {
          s.known = Math.max(s.known ?? revision, revision);
        })
        .catch(() => undefined);
    }
    if (remote) setRemoteChangeAt(Date.now());
    setTick((current) => current + 1);
    void client.invalidateQueries(
      { predicate: (query) => invalidates(batch, familyOf(query.queryKey)) },
      // A fetch already under way was started after the commit; let it answer.
      { cancelRefetch: false },
    );
  }, [client]);

  const enqueue = useCallback(
    (invalidation: Invalidation, origin: "local" | "remote") => {
      const s = state.current;
      s.batch = mergeInvalidations(s.batch, invalidation);
      if (origin === "local") s.local = true;
      else s.remote = true;
      if (!s.timer) s.timer = setTimeout(flush, BATCH_MS);
    },
    [flush],
  );

  // A batch held back for a mutation goes out when the last one settles.
  useEffect(
    () =>
      client.getMutationCache().subscribe(() => {
        const s = state.current;
        if (s.batch && !s.timer && client.isMutating() === 0) s.timer = setTimeout(flush, BATCH_MS);
      }),
    [client, flush],
  );
  useEffect(() => () => clearTimeout(state.current.timer), []);

  const localChange = useCallback(
    (families: readonly QueryFamily[] | "all") =>
      enqueue(families === "all" ? { all: true } : { all: false, families: new Set(families) }, "local"),
    [enqueue],
  );

  const agentsTimer = useRef<ReturnType<typeof setTimeout>>();
  useLiveEvent((event) => {
    const s = state.current;
    if (event.type === "revision") {
      s.eventsFlowing = true;
      if (typeof event.revision === "number") {
        s.known = Math.max(s.known ?? event.revision, event.revision);
        s.sawRevision = true;
      }
      enqueue(invalidationFor(event.tables), "remote");
      return;
    }
    if (event.type === "agent.message") {
      const message = event.message as AgentMessage;
      client.setQueryData<TranscriptData>(qk.agentMessages(event.agentId), (data) => upsertTranscript(data, [message]));
      // A question opening or closing is what the Awaiting list is about.
      if (message.kind === "question") void client.invalidateQueries({ queryKey: qk.awaiting() }, { cancelRefetch: false });
    }
    if (event.type === "agent.updated" || event.type === "agent.message") {
      clearTimeout(agentsTimer.current);
      agentsTimer.current = setTimeout(
        () => void client.invalidateQueries({ queryKey: qk.agents() }, { cancelRefetch: false }),
        AGENTS_DEBOUNCE_MS,
      );
    }
  });
  useEffect(() => () => clearTimeout(agentsTimer.current), []);

  // Back from a dropped socket: whatever was pushed meanwhile is lost, so re-read.
  // Only a *re*connect: the first connect comes a moment after the first fetches,
  // which already read the current state.
  const wasConnected = useRef(connected);
  const everConnected = useRef(connected);
  useEffect(() => {
    if (connected && !wasConnected.current && everConnected.current) {
      enqueue({ all: true }, "remote");
      refreshTranscripts(client);
    }
    if (connected) everConnected.current = true;
    wasConnected.current = connected;
  }, [connected, client, enqueue]);

  // The safety net: a push can be lost, and without Redis there are none at all.
  const baselined = useRef(false);
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { revision } = await api.revision();
        const s = state.current;
        // The baseline is kept even when the socket connecting has re-run this effect
        // meanwhile: dropped, the next poll took a baseline that already counted any
        // write made in between, and that write was never fetched.
        if (s.known === null) s.known = revision;
        else if (cancelled) return;
        else if (revision > s.known) {
          s.known = revision;
          enqueue({ all: true }, "remote");
        }
      } catch {
        // Offline shows up in the views' own errors; a server restarting should not flash one here.
      }
    };
    // Once, for a baseline: the socket connecting re-runs this effect a moment later.
    if (!baselined.current) {
      baselined.current = true;
      void check();
    }
    const handle = setInterval(check, connected ? POLL_MS_LIVE : POLL_MS);
    const onFocus = () => void check();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(handle);
      window.removeEventListener("focus", onFocus);
    };
  }, [connected, enqueue]);

  const value = useMemo(() => ({ localChange, remoteChangeAt, tick }), [localChange, remoteChangeAt, tick]);
  return <LiveSyncContext.Provider value={value}>{children}</LiveSyncContext.Provider>;
}
